import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { capturePageview } from './lib/analytics';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import AskAIWidget from './components/Layout/AskAIWidget';
import LoadingSpinner from './components/UI/LoadingSpinner';

// Pages
import Home from './pages/Home';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import OAuthCallback from './pages/Auth/OAuthCallback';
import VerifyEmail from './pages/Auth/VerifyEmail';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import Dashboard from './pages/Dashboard/Dashboard';
import StockDetail from './pages/Stocks/StockDetail';
import Watchlist from './pages/Stocks/Watchlist';
import StockChartPage from './pages/StockChartPage';
import StockAnalyze from './pages/Stocks/StockAnalyze';
import Strategy from './pages/Stocks/Strategy';
import AiRank from './pages/Stocks/AiRank';
import AskAI from './pages/Stocks/AskAI';
import SamplePick from './pages/Stocks/SamplePick';
import Pricing from './pages/Subscription/Pricing';
import Checkout from './pages/Subscription/Checkout';
import Account from './pages/Account/Account';
import Billing from './pages/Account/Billing';
import ContactUs from './pages/Contact/ContactUs';
import Admin from './pages/Admin/Admin';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import TermsOfService from './pages/Legal/TermsOfService';
import CookiePolicy from './pages/Legal/CookiePolicy';

import './index.css';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Fires a PostHog $pageview on every client-side route change. This is what
// captures anonymous visitors (homepage landings, browsing) who never sign up.
const AnalyticsTracker = () => {
  const location = useLocation();
  React.useEffect(() => {
    capturePageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
};

// Admin Route Component (admin-only). The server enforces admin on every
// /api/admin/* call; this guard is UX — it hides the page from non-admins.
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (!user.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/strategy" replace />;
  }

  return children;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Elements stripe={stripePromise}>
        <AuthProvider>
          <Router>
            <AnalyticsTracker />
            <div className="min-h-screen bg-gray-50 flex flex-col">
              <Navbar />
              
              <main className="flex-grow">
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/sample-pick" element={<SamplePick />} />
                  <Route path="/pricing" element={<Pricing />} />
                  
                  {/* Auth Routes */}
                  <Route 
                    path="/login" 
                    element={
                      <PublicRoute>
                        <Login />
                      </PublicRoute>
                    } 
                  />
                  <Route 
                    path="/register" 
                    element={
                      <PublicRoute>
                        <Register />
                      </PublicRoute>
                    } 
                  />

                  {/* OAuth redirect callback (Microsoft/Facebook/GitHub/LinkedIn/X/Amazon) */}
                  <Route path="/auth/callback" element={<OAuthCallback />} />

                  {/* Email verification link target */}
                  <Route path="/verify-email" element={<VerifyEmail />} />

                  {/* Password reset */}
                  <Route
                    path="/forgot-password"
                    element={
                      <PublicRoute>
                        <ForgotPassword />
                      </PublicRoute>
                    }
                  />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  {/* Protected Routes */}
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/stock/:symbol" 
                    element={
                      <ProtectedRoute>
                        <StockDetail />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/watchlist" 
                    element={
                      <ProtectedRoute>
                        <Watchlist />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/charts" 
                    element={
                      <ProtectedRoute>
                        <StockChartPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/stock/stock-analyze" 
                    element={<StockAnalyze />} 
                  />
                  <Route 
                    path="/stock/stock-analyze/:symbol" 
                    element={<StockAnalyze />} 
                  />
                  {/* Ask AI is PUBLIC (anonymous allowed; server meters per IP/day). */}
                  <Route 
                    path="/ask-ai" 
                    element={<AskAI />} 
                  />
                  <Route 
                    path="/strategy" 
                    element={
                      <ProtectedRoute>
                        <Strategy />
                      </ProtectedRoute>
                    } 
                  />
                  <Route
                    path="/ai-rank"
                    element={
                      <ProtectedRoute>
                        <AiRank />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/checkout/:planId" 
                    element={
                      <ProtectedRoute>
                        <Checkout />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/account" 
                    element={
                      <ProtectedRoute>
                        <Account />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/billing" 
                    element={
                      <ProtectedRoute>
                        <Billing />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Admin Routes (admin-only, server-enforced) — unified tabbed hub. */}
                  <Route
                    path="/admin"
                    element={
                      <AdminRoute>
                        <Admin />
                      </AdminRoute>
                    }
                  />
                  {/* Back-compat: old link redirects to the Admin hub. */}
                  <Route path="/admin/users" element={<Navigate to="/admin" replace />} />
                  {/* /admin/chat still works — the hub opens the Chat tab from the path. */}
                  <Route
                    path="/admin/chat"
                    element={
                      <AdminRoute>
                        <Admin />
                      </AdminRoute>
                    }
                  />

                  {/* Public Routes */}
                  <Route path="/contact" element={<ContactUs />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/cookies" element={<CookiePolicy />} />
                  />

                  {/* Catch all route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>

              <Footer />
            </div>

            {/* Floating Ask AI launcher (every page except /ask-ai) */}
            <AskAIWidget />

            {/* Toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  style: {
                    background: '#22c55e',
                  },
                },
                error: {
                  style: {
                    background: '#ef4444',
                  },
                },
              }}
            />
          </Router>
        </AuthProvider>
      </Elements>
    </QueryClientProvider>
  );
}

export default App;
